import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import SVG from 'react-inlinesvg';
import _compact from 'lodash/compact';
import handIcon from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import endCallIcon from '@lifesize/ux-assets/vectors/svgIcons/end-call.svg';
import micIcon from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import micOffIcon from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import moreIcon from '@lifesize/ux-assets/vectors/svgIcons/more.svg';
import presentIcon from '@lifesize/ux-assets/vectors/svgIcons/share-screen.svg';
import DropdownComp from 'components/Dropdown/Dropdown';
import Tooltip from 'components/Tooltip/Tooltip';
import classes from './InCallParticipant.module.scss';

class InCallParticipant extends React.PureComponent {
  handleLowerHandClicked = (e) => {
    e.preventDefault();
    this.props.onLowerHandClicked(this.props.participant.participantId);
  }

  handleMuteParticipantClicked = (e) => {
    e.preventDefault();
    this.props.onMuteParticipantClicked(this.props.participant);
  };

  handleRemoveFromMeetingClicked = (e) => {
    e.preventDefault();
    this.props.onRemoveFromMeetingClicked(this.props.participant);
  };

  handleSelectRaisedHandClicked = (e) => {
    e.preventDefault();
    this.props.onSelectRaisedHandClicked(this.props.participant.participantId);
  };

  handleStopPresenterClicked = (e) => {
    e.preventDefault();
    this.props.onStopPresenterClicked(this.props.participant);
  };

  getMicConfig = () => {
    const { userIsModerator } = this.props;
    const { localAudioMuteState, serverAudioMuteState } = this.props.participant;

    const config = {
      currentDataTestId: (serverAudioMuteState || localAudioMuteState) ? 'participantBtnMicOff' : 'participantBtnMicOn',
      currentIcon: serverAudioMuteState || localAudioMuteState ? micOffIcon : micIcon,
      currentIconClasses: _compact([
        classes.buttonIcon,
        userIsModerator && localAudioMuteState && !serverAudioMuteState ? classes.selfMuted : ''
      ]).join(' '),
    };

    if (serverAudioMuteState) {
      config.menuOptionDataTestId = 'moreMenuOptionMicOn';
      config.menuOptionIcon = micIcon;
      config.menuOptionLabel = (
        <FormattedMessage
          id="components.inCall.participantDrawer.removeModeratorMute"
          defaultMessage="Remove moderator mute"
        />
      );
    } else { // !serverAudioMuteState
      config.menuOptionDataTestId = 'moreMenuOptionMicOff';
      config.menuOptionIcon = micOffIcon;
      config.menuOptionLabel = (
        <FormattedMessage
          id="components.inCall.participantDrawer.addModeratorMute"
          defaultMessage="Add moderator mute"
        />
      );
    }

    return config;
  }

  renderMicButton() {
    const { canBeMuted, userIsModerator } = this.props;
    const { isSelf } = this.props.participant;
    const micConfig = this.getMicConfig();

    if (userIsModerator && canBeMuted && !isSelf) {
      return (
        <button className={classes.button} onClick={this.handleMuteParticipantClicked} data-testid={micConfig.currentDataTestId}>
          <SVG src={micConfig.currentIcon} className={micConfig.currentIconClasses} />
          <Tooltip
            fixPosition={true}
            label={micConfig.menuOptionLabel}
            location={'top'}
            overlapCarat={false}
            shift={'left'}
          />
        </button>
      );
    }

    // non-moderators just see an indicator
    return (
      <div className={`${classes.button} ${classes.noButton}`} data-testid={micConfig.currentDataTestId}>
        <SVG src={micConfig.currentIcon} className={micConfig.currentIconClasses} />
      </div>
    );
  }

  renderMoreButton() {
    const { canBeMuted, userIsModerator } = this.props;
    const { handRaised, isSelf } = this.props.participant;
    const micConfig = this.getMicConfig();

    if (userIsModerator && !isSelf) {
      return (
        <DropdownComp
          menuNoLeft
          menuNoTop
          buttonClassName={classes.dropdownButton}
          buttonComponent={(
            <div className={classes.button} data-testid="moreDropdownMenuTrigger">
              <SVG src={moreIcon} className={classes.buttonIcon} data-testid="moreIcon" />
            </div>
          )}
          containerClassName={classes.dropdown}
          openButtonComponent={(
            <div className={classes.button} data-testid="moreDropdownMenuTrigger">
              <SVG src={moreIcon} className={classes.buttonIcon} data-testid="moreIcon" />
            </div>
          )}
          dropdownClassName={classes.dropdownMenu}
          menuComponent={(
            <ul>
              {!!handRaised && (
                <li
                  data-testid="moreMenuOptionLowerHand"
                  onClick={this.handleLowerHandClicked}
                >
                  <SVG src={handIcon} className={classes.buttonIcon} />
                  <span>
                    <FormattedMessage
                      id="components.inCall.participantDrawer.lowerHand"
                      defaultMessage="Lower Hand"
                    />
                  </span>
                </li>
              )}
              {!!canBeMuted && (
                <li
                  data-testid={micConfig.menuOptionDataTestId}
                  onClick={this.handleMuteParticipantClicked}
                >
                  <SVG src={micConfig.menuOptionIcon} className={classes.buttonIcon} />
                  <span>
                    {micConfig.menuOptionLabel}
                  </span>
                </li>
              )}
              <li
                data-testid="moreMenuOptionRemoveFromMeeting"
                onClick={this.handleRemoveFromMeetingClicked}
              >
                <SVG src={endCallIcon} className={classes.buttonIcon} />
                <span>
                  <FormattedMessage
                    id="components.inCall.participantDrawer.removeFromMeeting"
                    defaultMessage="Remove from Meeting"
                  />
                </span>
              </li>
            </ul>
          )}
        />
      );
    }

    // non-moderators see nothing (blank space)
    return (
      <div className={`${classes.button} ${classes.noButton}`} />
    );
  }

  renderPresentationButton = () => {
    const { userIsModerator } = this.props;
    const { isPresenting } = this.props.participant;

    if (!isPresenting) return null;
    if (userIsModerator) {
      return (
        <button className={classes.button} onClick={this.handleStopPresenterClicked}>
          <SVG src={presentIcon} className={`${classes.buttonIcon} ${classes.presenterIcon}`} data-testid="menuIconPresent" />
        </button>
      );
    }
    return (
      <div className={`${classes.button} ${classes.noButton}`}>
        <SVG src={presentIcon} className={`${classes.buttonIcon} ${classes.presenterIcon}`} data-testid="menuIconPresent" />
      </div>
    );
  }

  renderRaisedHandButton = () => {
    const { userIsModerator } = this.props;
    const { handRaised, isSelf } = this.props.participant;

    if (!handRaised) return null;
    if (!!handRaised && userIsModerator && !isSelf) {
      return (
        <button className={classes.button} onClick={this.handleSelectRaisedHandClicked}>
          <SVG src={handIcon} className={`${classes.buttonIcon} ${classes.raisedHandIcon}`} />
          <Tooltip
            fixPosition={true}
            location={'top'}
            overlapCarat={false}
            label={
              <FormattedMessage
                id="components.inCall.participantDrawer.tooltip.lowerHand" defaultMessage="Click to Lower Hand"
              />
            }
          />
        </button>
      );
    }
    return (
      <div className={`${classes.button} ${classes.noButton}`}>
        <SVG src={handIcon} className={`${classes.buttonIcon} ${classes.raisedHandIcon}`} />
      </div>
    );
  }

  render() {
    const { userIsModerator } = this.props;
    const { dialString, isSelf, name } = this.props.participant;
    const participantClasses = _compact([
      classes.participant,
      userIsModerator && classes.isModerator,
      isSelf && classes.isSelf
    ]).join(' ');
    const participantNameClasses = _compact([
      classes.name,
      isSelf && classes.isSelf
    ]).join(' ');

    return (
      <div data-testid="participant-item" className={participantClasses}>
        <div data-testid="participant-name" className={participantNameClasses}>{name || dialString}</div>
        <div className={classes.buttons}>
          {this.renderPresentationButton()}
          {this.renderRaisedHandButton()}
          {this.renderMicButton()}
          {this.renderMoreButton()}
        </div>
      </div>
    );
  }
}

InCallParticipant.propTypes = {
  canBeMuted: PT.bool,
  onLowerHandClicked: PT.func.isRequired,
  onMuteParticipantClicked: PT.func.isRequired,
  onStopPresenterClicked: PT.func.isRequired,
  onRemoveFromMeetingClicked: PT.func.isRequired,
  onSelectRaisedHandClicked: PT.func.isRequired,
  participant: PT.object,
  userIsModerator: PT.bool
};

export default injectIntl(InCallParticipant);
